<template>
  <themed-modal
    :name="modalName"
    :title="title"
    size="lg"
    @closed="handleClosed"
  >
    <div v-if="row" class="row-details-table-wrapper">
      <table class="row-details-table">
        <tbody>
          <tr 
            v-for="column in visibleColumns"
            :key="column.field"
            class="row-details-table-row"
          >
            <th class="row-details-table-header">
              {{ column.label || column.field }}
            </th>
            <td class="row-details-table-cell">
              <!-- Slot for custom rendering (highest priority) -->
              <slot 
                v-if="hasSlot(column.field)"
                :name="`column-${column.field}`"
                :row="row"
                :value="getCellValue(column.field)"
                :column="column"
              ></slot>
              
              <!-- Formatter (second priority) -->
              <span 
                v-else-if="column.formatter"
                v-html="getFormattedValue(column)"
              ></span>
              
              <!-- Default value display with fallback -->
              <span v-else>{{ getDisplayValue(column) }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <!-- Loading state when no row data -->
    <div v-else class="row-details-loading">
      <p>Loading...</p>
    </div>
    
    <template #footer>
      <div class="themed-table-row-details-footer">
        <!-- Action buttons with icons only -->
        <themed-button
          v-for="action in visibleActions"
          :key="action.name"
          :variant="action.variant || 'ghost'"
          :size="action.size || 'sm'"
          @click="handleAction(action.name)"
          :title="action.label || action.name"
        >
          <theme-icon 
            v-if="action.icon"
            :icon="action.icon" 
            type="icons" 
            classes="w-4 h-4" 
          />
          <span v-else>{{ action.label || action.name }}</span>
        </themed-button>
      </div>
    </template>
  </themed-modal>
</template>

<script>
import ThemedModal from '../ThemedModal.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemeIcon from '../ThemeIcon.vue';

export default {
  name: 'RowDetailsPopup',
  components: {
    ThemedModal,
    ThemedButton,
    ThemeIcon
  },
  props: {
    modalName: {
      type: String,
      default: 'row-details-modal'
    },
    title: {
      type: String,
      default: 'Row Details'
    },
    row: {
      type: Object,
      default: null
    },
    columns: {
      type: Array,
      default: () => []
    },
    actions: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    visibleColumns() {
      // Filter out action columns
      return this.columns.filter(col => col.type !== 'actions');
    },
    
    visibleActions() {
      // Filter actions based on visibility
      return this.actions.filter(action => {
        if (action.visible === undefined) {
          return true;
        }
        
        if (typeof action.visible === 'boolean') {
          return action.visible;
        }
        
        if (typeof action.visible === 'function') {
          return action.visible(this.row);
        }
        
        return true;
      });
    }
  },
  methods: {
    hasSlot(field) {
      const slotName = `column-${field}`;
      return !!this.$slots[slotName] || !!this.$scopedSlots[slotName];
    },
    
    getCellValue(field, column) {
      if (!field || !this.row) {
        return null;
      }
      
      // Support nested field access with dot notation
      const fields = field.split('.');
      let value = this.row;
      
      for (const f of fields) {
        if (value && typeof value === 'object') {
          value = value[f];
        } else {
          return null;
        }
      }
      
      return value;
    },
    
    getFormattedValue(column) {
      const value = this.getCellValue(column.field);
      
      // If column has a formatter function, use it
      if (column.formatter && typeof column.formatter === 'function') {
        const formatted = column.formatter(value, this.row);
        
        // Return formatted value as-is (will be rendered with v-html in template)
        // If it's empty after formatting, show placeholder
        if (formatted === null || formatted === undefined || formatted === '') {
          return '-';
        }
        
        return formatted;
      }
      
      // Default formatting - show placeholder for empty values
      if (value === null || value === undefined || value === '') {
        return '-';
      }
      
      // Handle boolean values
      if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
      }
      
      return value;
    },
    
    getDisplayValue(column) {
      // This method handles display when there's no formatter or slot
      // It provides better fallback logic for missing fields
      
      const value = this.getCellValue(column.field);
      
      // If we got a value, format it
      if (value !== null && value !== undefined && value !== '') {
        // Handle boolean values
        if (typeof value === 'boolean') {
          return value ? 'Yes' : 'No';
        }
        
        // Handle arrays
        if (Array.isArray(value)) {
          return value.join(', ');
        }
        
        // Handle objects (show JSON or specific property)
        if (typeof value === 'object') {
          // Try common display properties
          if (value.name) return value.name;
          if (value.label) return value.label;
          if (value.title) return value.title;
          // Otherwise show JSON
          return JSON.stringify(value);
        }
        
        return value;
      }
      
      // No value found - try to infer from column label and row data
      // This helps when column.field doesn't match actual data structure
      if (this.row && column.label) {
        // Try to find a matching field by label (case-insensitive)
        const labelLower = column.label.toLowerCase().replace(/\s+/g, '_');
        const possibleFields = [
          labelLower,
          column.label.toLowerCase().replace(/\s+/g, ''),
          `${labelLower}_name`,
          `${labelLower}_id`
        ];
        
        for (const field of possibleFields) {
          if (this.row[field] !== undefined && this.row[field] !== null) {
            return this.row[field];
          }
        }
      }
      
      return '-';
    },
    
    close() {
      this.$modal.hide(this.modalName);
    },
    
    handleClosed() {
      this.$emit('closed');
    },
    
    handleAction(actionName) {
      // Store the row data before closing modal
      const rowData = this.row;
      
      // Close modal first
      this.close();
      
      // Emit action event after a short delay to ensure modal is closed
      this.$nextTick(() => {
        this.$emit('action', {
          action: actionName,
          row: rowData
        });
      });
    }
  }
}
</script>
